import { apiFetch } from '@carouselgg/auth/client'

/**
 * Everything the signed-in player is in, for the account hub's Competitions
 * page: their teams' entries (waiting, live and past), their own free-agent
 * signups, and the applications they sent to teams.
 */

/** One competition entry by a team the player plays on or runs. */
export interface MyEntry {
  season_id: string
  name: string
  slug: string
  /** The competition's status. */
  status: 'open' | 'active' | 'playoffs' | 'completed' | 'cancelled' | string
  competition_type: string
  starts_at: string | null
  ends_at: string | null
  in_person: boolean
  league_name: string
  league_slug: string
  game_name: string
  game_logo_url: string
  team_id: string
  team_name: string
  team_slug: string
  team_org_slug: string
  registration_id: string
  registration_status: 'pending' | 'approved' | 'active' | 'rejected' | 'withdrawn' | string
  /** The player's role on the team, or in the club fielding it. */
  team_role: string
  reason: 'playing' | 'team'
  /** The rules version the team still has to agree to; 0 when nothing is owed. */
  rules_to_agree: number
}

/** A free-agent signup: a competition's pool, or a request to join a club. */
export interface MySignup {
  id: string
  status: 'pending' | 'open' | 'placed' | 'withdrawn' | 'expired' | 'rejected' | string
  message: string
  created_at: string
  organization_id: string
  organization_name: string
  organization_slug: string
  game_name: string
  season_id: string | null
  season_name: string
  season_slug: string
  team_name: string
}

/** An application the player sent to a team. */
export interface MyApplication {
  id: string
  status: 'pending' | 'tryout' | 'approved' | 'rejected' | 'withdrawn' | string
  message: string
  review_note: string
  created_at: string
  reviewed_at: string | null
  team_name: string
  team_slug: string
  organization_id: string
  organization_name: string
  organization_slug: string
  season_name: string
  season_slug: string
  league_slug: string
  game_name: string
}

export interface MyActivity {
  entries: MyEntry[]
  signups: MySignup[]
  applications: MyApplication[]
}

export function getMyActivity(): Promise<MyActivity> {
  return apiFetch<MyActivity>('/compete/mine/everything')
}

/** Takes the player out of a free-agent pool, or withdraws a club join request. */
export function withdrawSignup(s: Pick<MySignup, 'id' | 'organization_id'>): Promise<unknown> {
  return apiFetch(`/compete/${s.organization_id}/free-agents/${s.id}`, { method: 'DELETE' })
}

/** Withdraws an application the player sent to a team. */
export function withdrawApplication(a: Pick<MyApplication, 'id' | 'organization_id'>): Promise<unknown> {
  return apiFetch(`/recruit/${a.organization_id}/applications/${a.id}`, { method: 'DELETE' })
}
